<template>
  <div class="magazine-article" data-track-as="magazine">
    <header-image
      v-if="headerImage"
      :image="headerImageObject"
      class="magazine-article-header-image"
    />
    <div class="content-area">
      <div class="content-column">
        <div
          v-if="registrationLabel || socialLabel"
          class="header-meta-area"
          data-track-skip
        >
          <div
            v-if="registrationLabel || socialLabel"
            class="header-meta-links"
          >
            <div
              v-if="socialLabel"
              class="registration-link"
              @click="showSocial"
            >
              <div>
                <dotcom-icon name="share-nodes" art="light" />
              </div>
              <div>{{ socialLabel }}</div>
            </div>
            <div
              v-if="registrationLabel"
              class="registration-link"
              @click="showRegistrationForm"
            >
              <div>
                <dotcom-icon name="envelope" art="light" />
              </div>
              <div>{{ registrationLabel }}</div>
            </div>
          </div>
          <div v-if="registrationForm.visible" class="registration-form">
            <a
              href
              class="registration-close"
              @click.prevent="hideRegistrationForm"
            >
              <dotcom-icon
                name="xmark"
                art="light"
                color="--c-secondary-neutral-2"
              />
            </a>
            <form
              v-if="
                registrationForm.state === 'form' ||
                registrationForm.state === 'waiting'
              "
              class="sanitasForms"
              @submit.prevent="registrationSubmit"
            >
              <h3>{{ registrationFormTitle }}</h3>
              <drop-down
                v-model="registrationForm.salutation"
                required
                :title-label="registrationFormSalutationLabel"
                :options="salutations"
                :errors="salutationErrors"
              />
              <text-input
                v-model="registrationForm.lastname"
                required
                :title-label="registrationFormLastnameLabel"
                :errors="lastnameErrors"
              />
              <text-input
                v-model="registrationForm.firstname"
                required
                :title-label="registrationFormFirstnameLabel"
                :errors="firstnameErrors"
              />
              <text-input
                v-model="registrationForm.email"
                required
                :title-label="registrationFormEmailLabel"
                :errors="emailErrors"
              />
              <sanitas-button
                class="registration-submit-button"
                primary
                type="submit"
                :is-spinner-active="registrationForm.state === 'waiting'"
              >
                {{ registrationFormSubmitLabel }}
              </sanitas-button>
              <div>{{ registrationFormMandatoryLabel }}</div>
            </form>
            <div v-if="registrationForm.state === 'success'" class="message">
              <h3>{{ registrationFormSuccessTitle }}</h3>
              <div>{{ registrationFormSuccessMessage }}</div>
            </div>
            <div v-if="registrationForm.state === 'error'" class="message">
              {{ registrationFormSubmitError }}
            </div>
          </div>
          <MagazineSocialSharing
            v-if="socialMagazineProps.visibility"
            :social-magazine-props="socialMagazineProps"
            :links="socialLinks"
            @hideSocial="hideSocial"
          >
            <template slot="title">
              {{ socialTitle }}
            </template>
          </MagazineSocialSharing>
        </div>
        <slot></slot>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { validationMixin } from 'vuelidate'
import { required } from 'vuelidate/lib/validators'

import HeaderImage from '@/components/HeaderImage'
import { setTrackerVersion, trackForm } from '@/helpers/tracker'
import { email } from '@/helpers/validation'
import MagazineSocialSharing from '@/modules/magazine/article/MagazineSocialSharing'

export default {
  name: 'SanitasMagazineArticle',

  components: {
    DotcomIcon: () => {
      return import(
        /* webpackChunkName: "dotcom-icon" */
        '@/modules/icon/Icon'
      )
    },
    HeaderImage,
    DropDown: () => {
      return import(
        /* webpackChunkName: "sanitas-components" */
        '@/components/sanitas-components/DropDown'
      )
    },
    TextInput: () => {
      return import(
        /* webpackChunkName: "sanitas-components" */
        '@/components/sanitas-components/TextInput'
      )
    },
    SanitasButton: () => {
      return import(
        /* webpackChunkName: "sanitas-components" */
        '@/components/sanitas-components/SanitasButton'
      )
    },
    MagazineSocialSharing,
  },

  mixins: [validationMixin],

  props: {
    headerImageRatio: {
      type: [Number, String],
      default: 0.5625,
    },
    headerImage: {
      type: String,
      default: '',
    },
    headerImageSrcset: {
      type: String,
      default: '',
    },
    registrationLabel: {
      type: String,
      default: '',
    },
    registrationFormTitle: {
      type: String,
      default: '',
    },
    registrationFormSalutationLabel: {
      type: String,
      default: '',
    },
    registrationFormSalutationMLabel: {
      type: String,
      default: '',
    },
    registrationFormSalutationFLabel: {
      type: String,
      default: '',
    },
    registrationFormSalutationError: {
      type: String,
      default: '',
    },
    registrationFormFirstnameLabel: {
      type: String,
      default: '',
    },
    registrationFormFirstnameError: {
      type: String,
      default: '',
    },
    registrationFormLastnameLabel: {
      type: String,
      default: '',
    },
    registrationFormLastnameError: {
      type: String,
      default: '',
    },
    registrationFormEmailLabel: {
      type: String,
      default: '',
    },
    registrationFormEmailError: {
      type: String,
      default: '',
    },
    registrationFormSubmitLabel: {
      type: String,
      default: '',
    },
    registrationFormLanguageCode: {
      type: String,
      default: '',
    },
    registrationFormMandatoryLabel: {
      type: String,
      default: '',
    },
    registrationFormSubmitUrl: {
      type: String,
      default: '',
    },
    registrationFormFormid: {
      type: String,
      default: '',
    },
    registrationFormFormstart: {
      type: String,
      default: '',
    },
    registrationFormSuccessTitle: {
      type: String,
      default: '',
    },
    registrationFormSuccessMessage: {
      type: String,
      default: '',
    },
    registrationFormSubmitError: {
      type: String,
      default: '',
    },
    socialLabel: {
      type: String,
      default: '',
    },
    socialTitle: {
      type: String,
      default: '',
    },
    socialLinks: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      registrationForm: {
        salutation: null,
        lastname: '',
        firstname: '',
        email: '',
        state: 'form',
        visible: false,
      },
      socialMagazineProps: {
        visibility: false,
        icon: {
          color: '#00765a',
          size: 40,
        },
      },
    }
  },

  validations: {
    registrationForm: {
      salutation: {
        required,
      },
      lastname: {
        required,
      },
      firstname: {
        required,
      },
      email: {
        required,
        email,
      },
    },
  },

  computed: {
    headerImageObject() {
      return {
        src: this.headerImage,
        srcSet: this.headerImageSrcset,
        ratio: this.headerImageRatio,
      }
    },
    sources() {
      const sourceSet = this.headerImageSrcset.split(',')
      const sources = []
      sourceSet.forEach(set => {
        const source = set.trim().split(' ')
        const size = source[1].match('(.*)w')
        if (size && size[1]) {
          sources.unshift({
            image: source[0],
            size: `(min-width: ${size[1]}px)`,
          })
        }
      })
      return sources
    },
    salutations() {
      return [
        {
          name: this.registrationFormSalutationFLabel,
          value: 2,
        },
        {
          name: this.registrationFormSalutationMLabel,
          value: 1,
        },
      ]
    },
    salutationErrors() {
      return this.$v.registrationForm.salutation.$invalid &&
        this.$v.registrationForm.salutation.$dirty
        ? { errors: this.registrationFormSalutationError }
        : null
    },
    lastnameErrors() {
      return this.$v.registrationForm.lastname.$invalid &&
        this.$v.registrationForm.lastname.$dirty
        ? { errors: this.registrationFormLastnameError }
        : null
    },
    firstnameErrors() {
      return this.$v.registrationForm.firstname.$invalid &&
        this.$v.registrationForm.firstname.$dirty
        ? { errors: this.registrationFormFirstnameError }
        : null
    },
    emailErrors() {
      return this.$v.registrationForm.email.$invalid &&
        this.$v.registrationForm.email.$dirty
        ? { errors: this.registrationFormEmailError }
        : null
    },
  },

  beforeMount() {
    setTrackerVersion()

    /* To avoid div's automatically inserted by AEM from having effects on the frontend CSS or markup structure,
       we strip away all div.parbase tags during initialization of the top-level magazine article component.
    */
    function stripParbase(inArray) {
      let outArray = []
      inArray.forEach(element => {
        if (
          element.tag === 'div' &&
          element.data &&
          element.data.staticClass &&
          element.data.staticClass.match(/\bparbase\b/)
        ) {
          if (element.children) {
            outArray.push(...element.children)
          }
        } else {
          outArray.push(element)
        }
      })
      outArray.forEach(element => {
        if (element.children) {
          element.children = stripParbase(element.children)
        }
        if (element.componentOptions && element.componentOptions.children) {
          element.componentOptions.children = stripParbase(
            element.componentOptions.children,
          )
        }
      })
      return outArray
    }

    /* In AEM, there is an element "Section separator" that renders an empty div. If we encounter
       such a div within an article tag, we render section tags accordingly, while ignoring the
       initial title, lead and author tags
    */
    function splitSections(inArray, createElement) {
      inArray.forEach(element => {
        if (
          element.tag === 'article' &&
          element.children &&
          element.children.length
        ) {
          let inMainBody = false
          let createSections = false
          let newChildren = []
          let nextChildren = []
          element.children.forEach(element => {
            if (
              element.tag === 'div' &&
              element.data &&
              element.data.staticClass &&
              element.data.staticClass.match(
                /\bsan-editorial-sectionseparator\b/,
              )
            ) {
              inMainBody = false
              createSections = true
              if (nextChildren.length) {
                newChildren.push(createElement('section', nextChildren))
                nextChildren = []
              }
            } else if (!inMainBody) {
              if (
                !element.tag ||
                element.tag === 'h1' ||
                (element.data &&
                  element.data.staticClass &&
                  element.data.staticClass.match(/\b(lead|author)\b/))
              ) {
                newChildren.push(element)
              } else {
                inMainBody = true
                nextChildren.push(element)
              }
            } else {
              nextChildren.push(element)
            }
          })
          if (nextChildren.length) {
            if (createSections) {
              newChildren.push(createElement('section', nextChildren))
            } else {
              newChildren.push(...nextChildren)
            }
          }
          element.children = newChildren
        }
      })
    }

    if (this.$slots.default && this.$slots.default.length) {
      this.$slots.default = stripParbase(this.$slots.default)
      splitSections(this.$slots.default, this.$createElement)
    }
  },

  methods: {
    showRegistrationForm() {
      this.registrationForm.visible = true
      trackForm('Main18SanitasNewsletterMagazin', 'ComFormStarted')
    },
    showSocial() {
      this.socialMagazineProps.visibility = true
    },
    hideSocial() {
      this.socialMagazineProps.visibility = false
    },
    hideRegistrationForm() {
      this.registrationForm.visible = false
      if (this.registrationForm.state === 'error') {
        this.registrationForm.state = 'form'
      }
    },
    registrationSubmit() {
      this.$v.$reset()
      this.$v.$touch()
      if (!this.$v.$invalid) {
        let registrationForm = this.registrationForm
        let data = new FormData()
        data.append('firstname', registrationForm.firstname)
        data.append('lastname', registrationForm.lastname)
        data.append('email', registrationForm.email)
        data.append('language', this.registrationFormLanguageCode)
        data.append('salutation', registrationForm.salutation)
        data.append(':formid', this.registrationFormFormid)
        data.append(':formstart', this.registrationFormFormstart)
        data.append('_charset_', 'UTF-8')
        data.append(':redirect', '/')

        registrationForm.state = 'waiting'
        trackForm('newsletter', 'ComFormSubmitted')

        axios({
          method: 'post',
          url: this.registrationFormSubmitUrl,
          data: data,
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        })
          .then(function (response) {
            registrationForm.state = 'success'
            checkRegistrationMessageScroll()
          })
          .catch(function (response) {
            registrationForm.state = 'error'
            checkRegistrationMessageScroll()
          })
      }

      function checkRegistrationMessageScroll() {
        // Scroll message into viewport on mobiles
        if (
          Math.max(
            document.documentElement.clientWidth,
            window.innerWidth || 0,
          ) < 480
        ) {
          if (window.scrollY > 280) {
            window.scrollBy(0, 260 - window.scrollY)
          }
        }
      }
    },
  },
}
</script>

<style lang="postcss">
/* Base element styling */
.magazine-article sup {
  top: -0.2em !important;
}

.magazine-article .content-area {
  --magazine-article-content-width-default: 800px;

  h1 {
    font-family: 'Faustina', serif;
    font-weight: 700;
    font-size: 60px;
    line-height: 70px;
    margin-bottom: 40px;
    color: var(--c-primary-neutral-1);
    overflow-wrap: break-word;
  }

  h3 {
    font-family: 'Faustina', serif;
  }

  h2,
  h4 {
    /* Overwrite global h4 font family TheMix Bold */
    font-family: 'Open Sans', sans-serif;
    font-size: 20px;
    font-weight: 700;
    line-height: 1.5;
    color: inherit;
  }

  p {
    font-family: 'Faustina', serif;
    font-weight: 400;
    font-size: 20px;
    line-height: 1.5;
    margin-bottom: 30px;
    color: var(--c-primary-neutral-1);

    a {
      border-bottom: none;
      font-family: inherit;
      font-size: inherit;
      display: inline-flex;

      .dotcom-icon {
        left: -24px;
      }

      &:hover {
        color: var(--c-primary-color-3);
        border-bottom: 1px solid var(--c-primary-color-3);
      }
    }
  }

  dl {
    font-size: 20px;
    line-height: 1.5;
    margin-top: 0;
    margin-bottom: 40px;

    dd {
      font-size: 20px;
      font-weight: 700;
      line-height: 1.5;
      margin-left: 0;
      color: var(--c-primary-neutral-1);
    }

    dt + dd {
      margin-top: 40px;
    }

    dt {
      font-family: 'Faustina', serif;
    }
  }

  /* TODO: remove duplicated styles in MagazineQuote */

  blockquote {
    font-family: 'Faustina', serif;
    font-weight: 700;
    font-size: 24px;
    line-height: 1.46;
    text-align: center;
    margin-bottom: 40px;

    footer {
      font-weight: 400;
      font-size: 15px;
      margin-top: 14px;
    }

    &:before {
      content: '';
      display: block;
      margin: 0 auto 40px;
      width: 160px;
      border: 1px solid var(--c-primary-neutral-1);
    }

    &:after {
      content: '';
      display: block;
      margin: 40px auto 0;
      width: 160px;
      border: 1px solid var(--c-primary-neutral-1);
    }
  }

  h2 + ul {
    margin-top: 20px;
  }

  ul {
    margin-left: 0;
    font-weight: 400;
    font-size: 20px;
    line-height: 1.5;
    margin-bottom: 40px;
    color: var(--c-primary-neutral-1);
    overflow-wrap: break-word;
    list-style: disc;
    padding-left: 21px;
    list-style-position: outside;
  }

  li {
    font-family: 'Faustina', serif;
    font-size: 20px;
    background: none;
    line-height: 27px;
    text-align: left;
    padding-left: 0;
    padding-bottom: 5px;
    color: var(--c-primary-neutral-1);

    b {
      font-weight: 700;
      color: var(--c-primary-neutral-1);
      line-height: 30px;
    }
  }

  ol {
    padding-left: 18px;
    list-style-position: outside;

    li {
      list-style-type: inherit;
      text-indent: 0;

      &:before {
        content: none;
      }
    }
  }

  sup {
    top: -0.2em !important;
  }
}

.magazine-article .content-area .san-richtext {
  margin-bottom: 40px;
}

@media (max-width: 490px) {
  .magazine-article .content-area {
    h1 {
      font-size: 32px;
      line-height: 35px;
    }

    h2 {
      font-size: 15px;
    }

    p {
      font-size: 15px;
    }

    dl,
    dl dd {
      font-size: 15px;
    }

    ol {
      padding-left: 14px;
    }
  }

  .magazine-article .content-area .san-richtext {
    li {
      font-size: 15px;
    }
  }
}

/* Styles based on classes */
.magazine-article {
  background-color: var(--c-primary-neutral-2);

  margin-left: calc(-1 * var(--body-offset-horizontal));
  margin-right: calc(-1 * var(--body-offset-horizontal));
  margin-bottom: -50px;

  #prefixbox,
  #suffixbox {
    padding: 0 !important;
    width: auto !important;
  }

  .content-area {
    background-color: white;
    max-width: 1100px;
    width: 100%;
    margin-left: auto;
    margin-right: auto;
    position: relative;
    color: var(--c-primary-neutral-1);

    .content-column {
      & > *:not(article),
      & > article > *:not(section),
      & section > * {
        width: var(--magazine-article-content-width-default);
        margin-left: auto;
        margin-right: auto;
      }

      .accordion {
        margin-bottom: 40px;
      }

      .header-meta-area {
        position: absolute;
        right: 0;
        top: 0;
        width: 100%;
        padding: 0;

        .header-meta-links {
          display: flex;
          justify-content: flex-end;
          position: absolute;
          top: 30px;
          right: 40px;
        }

        .registration-form {
          background-color: white;
          border-bottom: 1px solid var(--c-primary-neutral-1);
          padding: 30px;
          position: relative;
          z-index: 10;

          h3 {
            font-size: 20px;
            margin-bottom: 30px;
            font-weight: bold;
            color: var(--c-primary-neutral-1);
          }

          form {
            max-width: 800px;
            margin-left: auto;
            margin-right: auto;

            button.sanitas-button {
              margin-top: 30px;
              margin-bottom: 30px;
            }
          }

          div.message {
            max-width: 800px;
            margin-left: auto;
            margin-right: auto;
            padding-bottom: 30px;
          }

          .registration-close {
            position: absolute;
            top: 15px;
            right: 15px;

            &:hover {
              text-decoration: none;
              border-bottom: none;
            }
          }
        }
      }

      .registration-link {
        display: flex;
        align-items: center;
        font-size: 11px;
        color: var(--c-primary-neutral-1);
        font-weight: 700;
        font-style: normal;
        line-height: 1.1;
        cursor: pointer;

        &:hover {
          text-decoration: none;
          border-bottom: none;
          color: var(--c-tint-color-1);
        }

        div + div {
          margin-left: 5px;
        }

        & + .registration-link {
          margin-left: 20px;
        }
      }
    }

    .taglabel.header {
      font-size: 15px;
      font-weight: bold;
      line-height: normal;
      color: var(--c-primary-color-2);
      display: table;
      margin-top: 10px;

      &:hover {
        color: var(--c-primary-color-3);
        border-bottom: none;
      }
    }

    .taglabel.header + h1 {
      line-height: 1.1;
    }

    .lead {
      font-size: 24px;
      line-height: 1.38;
      margin-bottom: 40px;
    }

    .author {
      font-size: 12px;
      color: var(--c-primary-neutral-1);
      margin-bottom: 80px;
    }
  }
}

.magazine-article-header-image + .content-area {
  margin-top: -200px;
}

@media (max-width: 1200px) {
  .magazine-article {
    margin-left: calc(-1 * var(--body-offset-horizontal));
    margin-right: calc(-1 * var(--body-offset-horizontal));
  }
}

@media (max-width: 1100px) {
  .magazine-article-header-image + .content-area {
    margin-top: 0;
  }
}

@media (max-width: 860px) {
  .magazine-article .content-area .content-column {
    width: 100%;

    & > *:not(article),
    & > article > *:not(section),
    & section > * {
      padding-left: 30px;
      padding-right: 30px;
      width: 100%;
    }

    .header-meta-area {
      position: relative;
      height: 30px;

      .header-meta-links {
        justify-content: flex-start;
        left: 30px;
        top: 15px;
        right: auto;
      }
    }
  }
}

@media (max-width: 490px) {
  .magazine-article {
    margin-left: calc(-1 * var(--body-offset-horizontal));
    margin-right: calc(-1 * var(--body-offset-horizontal));

    .content-area {
      .content-column {
        width: 100%;

        & > *:not(article),
        & > article > *:not(section),
        & section > * {
          padding-left: 10px;
          padding-right: 10px;
        }

        .header-meta-area .header-meta-links {
          left: 10px;
        }
      }

      .lead {
        font-size: 20px;
        line-height: 1.5;
      }

      div.author {
        font-size: 12px;
      }
    }
  }
}
</style>

<style scoped>
.registration-link >>> .dotcom-icon {
  font-size: 18px;
}

.magazine-article .content-area .content-column .magazine-article-breadcrumb {
  margin: 0 0 0 0;
  min-height: 0;

  >>> ul {
    margin-bottom: 0;
    font-size: 12px;
  }
}

@media (--xs) {
  .magazine-article .content-area .content-column .magazine-article-breadcrumb {
    >>> ul {
      font-size: 16px;
    }
  }
}

@media (min-width: 860px) {
  .magazine-article .content-area .content-column .magazine-article-breadcrumb {
    min-height: var(--spacing-16);
    margin: 0 0 0 40px;
  }
}
</style>
